import { useEffect } from "react";
import { SITES, HEIGHTS, MAX_FORECAST_TIME, getApiBaseUrl } from "../config/siteConfig";

/**
 * This is a hook that fetches and caches data from the server
 * @param {Object} cachedData - Current cached data
 * @param {Function} setCachedData - Function to update cached data
 */
function useFetchAndCacheData(cachedData, setCachedData) {
    useEffect(() => {
        const fetchDataSequentially = async () => {
            // Get the API base URL from config
            const apiBaseUrl = getApiBaseUrl();
            
            // Object to accumulate all data
            let allData = { ...cachedData }; // Clone to preserve any data already loaded by single site loader

            // Fetch data for each site
            for (const site of SITES) {
                const siteIndex = site.id;
                if (!allData[siteIndex]) {
                    allData[siteIndex] = {};
                }
                
                // First, fetch positions if not cached
                if (allData[siteIndex]?.positions === undefined) {
                    try {
                        // Use full server URL from config
                        const url = `${apiBaseUrl}/positions/${siteIndex}`;
                        
                        const positionsResponse = await fetch(url, {
                            cache: "no-store"
                        });
                        const positionsData = await positionsResponse.json();
                        console.log(`Positions count for site ${siteIndex}: ${positionsData.positions.length}`);
                        allData[siteIndex].positions = positionsData.positions;
                    } catch (error) {
                        console.error(`Error fetching positions for site ${siteIndex}:`, error);
                    }
                } else {
                    console.log(`Already have positions for site ${siteIndex} from prioritized loading`);
                }

                // Fetch data for each height
                for (const height of HEIGHTS) {
                    if (!allData[siteIndex][height]) {
                        allData[siteIndex][height] = {};
                    }
                    
                    // Skip if we already have all data for this height
                    let allTimesLoaded = true;
                    for (let forecastTime = 0; forecastTime <= MAX_FORECAST_TIME; forecastTime++) {
                        if (!allData[siteIndex][height][forecastTime]) {
                            allTimesLoaded = false;
                            break;
                        }
                    }
                    
                    if (allTimesLoaded) {
                        console.log(`Already have all data for site ${siteIndex}, height ${height}`);
                        continue;
                    }
                    
                    console.log(`Loading data for site ${siteIndex}, height ${height}...`);

                    // Fetch data for each forecast time
                    try {
                        for (let forecastTime = 0; forecastTime <= MAX_FORECAST_TIME; forecastTime++) {
                            // Skip if already loaded by single site loader
                            if (allData[siteIndex][height][forecastTime]) {
                                console.log(`Already have data for site ${siteIndex}, height ${height}, time ${forecastTime} from prioritized loading`);
                                continue;
                            }
                            
                            // Use full server URL from config
                            const timeUrl = `${apiBaseUrl}/data/${siteIndex}/${height}/${forecastTime}`;
                            
                            const timeResponse = await fetch(timeUrl, {
                                cache: "no-store"
                            });
                            if (!timeResponse.ok) throw new Error(`HTTP error! status: ${timeResponse.status}`);
                            const data = await timeResponse.json();
                            allData[siteIndex][height][forecastTime] = { directions: data.directions };

                            console.log(`Loaded data for site ${siteIndex}, height ${height}, time ${forecastTime}`);

                            // Update cache with first batch of data to improve perceived performance
                            if(siteIndex === SITES[0].id && height === HEIGHTS[0] && forecastTime === 0){
                                console.log(`First Load Set Cached Data: ${data.directions.length} directions`);
                                setCachedData({...allData});
                            }
                        }
                        
                        // After loading all time forecasts for this site/height, update the cache
                        console.log(`All time forecasts loaded for site ${siteIndex}, height ${height}. Updating cache.`);
                        setCachedData({...allData});
                    } catch (error) {
                        console.error(`Error fetching data for site ${siteIndex}, height ${height}:`, error);
                    }
                }
            }

            // Finally, update all cached data at once
            setCachedData(prev => ({
                ...prev,
                ...allData
            }));
        };

        fetchDataSequentially();
    }, []);
}

export default useFetchAndCacheData;